import { useEffect, useState } from "react";
import type { MessagerieSettings, OutboxSummary } from "@shared/modules/x-messagerie";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Field, Input } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Toggle } from "../../../core/components/ui/toggle";
import { useToastStore } from "../../../core/stores/toast";
import { getSettings, getSummary, sendTestEmail, updateSettings } from "../lib/api";

const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Paramètres de la messagerie (onglet « Paramètres ») : lien d'avis, coordonnées, heures des automatisations, destinataires du récapitulatif et des avis d'annulation. */
export function ReglagesPage() {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<MessagerieSettings | null>(null);
  const [summary, setSummary] = useState<OutboxSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const test = async () => {
    if (testing || !testTo.trim()) return;
    setTesting(true);
    try {
      const m = await sendTestEmail(testTo.trim());
      if (m.status === "envoye") push("success", "Courriel de test envoyé", `À ${m.toAddress}.`);
      else if (m.status === "simule")
        push("warning", "Courriel simulé", "Aucune clé Mailjet sur ce serveur : rien n'est parti.");
      else push("danger", "Envoi en échec", m.error ?? undefined);
    } catch {
      push("danger", "Envoi impossible", "Vérifiez l'adresse.");
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    getSettings()
      .then(setDraft)
      .catch(() => push("danger", "Impossible de charger les réglages"));
    getSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [push]);

  const set = <K extends keyof MessagerieSettings>(key: K, value: MessagerieSettings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      setDraft(await updateSettings(draft));
      push("success", "Paramètres enregistrés");
    } catch {
      push(
        "danger",
        "Enregistrement impossible",
        "Vérifiez le lien d'avis (adresse complète), les heures et le cellulaire (dix chiffres).",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Paramètres de la messagerie"
        subtitle="Ce que les modèles insèrent et quand les automatisations partent."
      />

      <div className="settings-grid">
        <Card
          title="Atelier"
          subtitle="Insérés dans les messages par {{adresseAtelier}} et {{lienAvis}}."
          footer={
            <Button variant="primary" onClick={save} disabled={busy || !draft}>
              Enregistrer
            </Button>
          }
        >
          {draft ? (
            <div className="form-grid">
              <div className="form-grid__full">
                <Field
                  label="Lien d'avis Google"
                  hint="Adresse complète de la page d'avis Google Business."
                >
                  <Input
                    type="url"
                    value={draft.reviewLink}
                    onChange={(e) => set("reviewLink", e.target.value)}
                    placeholder="https://g.page/r/…/review"
                  />
                </Field>
              </div>
              <div className="form-grid__full">
                <Field label="Adresse de l'atelier">
                  <Input
                    value={draft.workshopAddress}
                    onChange={(e) => set("workshopAddress", e.target.value)}
                    placeholder="ex : 12 rue Principale, Québec"
                  />
                </Field>
              </div>
              <Field label="Téléphone de l'atelier">
                <Input
                  type="tel"
                  value={draft.workshopPhone}
                  onChange={(e) => set("workshopPhone", e.target.value)}
                  placeholder="ex : 418 555-0134"
                />
              </Field>
              <Field label="Nom d'expéditeur" hint="Nom affiché sur les courriels.">
                <Input
                  value={draft.senderName}
                  onChange={(e) => set("senderName", e.target.value)}
                />
              </Field>
            </div>
          ) : (
            <p className="messages__muted">Chargement…</p>
          )}
        </Card>

        <Card title="Automatisations" subtitle="Heures locales (Montréal).">
          {draft ? (
            <div className="form-grid">
              <Field
                label="Rappel avant le rendez-vous (heures)"
                hint="24 = la veille à la même heure."
              >
                <Input
                  type="number"
                  min={1}
                  max={168}
                  value={draft.reminderHoursBefore}
                  onChange={(e) => set("reminderHoursBefore", Number(e.target.value))}
                />
              </Field>
              <Field
                label="Délai du lien d'avis (heures)"
                hint="Après achèvement. Laisser vide pour utiliser une heure fixe."
              >
                <Input
                  type="number"
                  min={0}
                  max={168}
                  step={0.5}
                  value={draft.reviewDelayHours ?? ""}
                  onChange={(e) =>
                    set("reviewDelayHours", e.target.value === "" ? null : Number(e.target.value))
                  }
                />
              </Field>
              <Field
                label="Heure d'envoi du lien d'avis"
                hint="Le jour où le véhicule est prêt, ou le lendemain si l'heure est passée."
              >
                <select
                  className="select"
                  disabled={draft.reviewDelayHours != null}
                  value={draft.reviewSendHour}
                  onChange={(e) => set("reviewSendHour", Number(e.target.value))}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h} h 00
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Heure du récapitulatif">
                <select
                  className="select"
                  value={draft.recapHour}
                  onChange={(e) => set("recapHour", Number(e.target.value))}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h} h 00
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Destinataire du récapitulatif"
                hint="Courriel ou numéro de téléphone. Vide = pas de récapitulatif."
              >
                <Input
                  value={draft.recapRecipient}
                  onChange={(e) => set("recapRecipient", e.target.value)}
                  placeholder="ex : sacha@protecto.ca"
                />
              </Field>
              <div className="form-grid__full">
                <Field
                  label="Cellulaire averti des annulations"
                  hint="Reçoit un texto à chaque rendez-vous annulé (client, téléphone, horaire, service). Vide = aucun texto. Le client, lui, n'est pas averti."
                >
                  <Input
                    type="tel"
                    value={draft.cancellationNotifyPhone}
                    onChange={(e) => set("cancellationNotifyPhone", e.target.value)}
                    placeholder="ex : 418 555-0134"
                  />
                </Field>
              </div>
              <div className="form-grid__full">
                <Toggle
                  checked={draft.winterOfferEnabled}
                  onChange={(v) => set("winterOfferEnabled", v)}
                  label="Offre de protection de peinture après un antirouille"
                  hint="Une heure après chaque réservation d'un antirouille (entre 9 h et 20 h), le client reçoit l'offre : texto s'il a accepté les textos, sinon courriel. Jamais deux fois en 90 jours, ni si le rendez-vous comprend déjà une cire, un scellant ou une protection de peinture. Texte : modèle « Offre protection de peinture »."
                />
              </div>
              <Field label="Rabais de l'offre (%)" hint="Remplace {{rabaisOffre}} dans le modèle.">
                <Input
                  type="number"
                  min={1}
                  max={90}
                  value={draft.winterOfferDiscountPercent}
                  onChange={(e) => set("winterOfferDiscountPercent", Number(e.target.value))}
                />
              </Field>
            </div>
          ) : null}
        </Card>

        <Card
          title="Fournisseurs d'envoi"
          subtitle="Clés lues dans le fichier .env du serveur (TWILIO_*, MAILJET_*)."
        >
          <dl className="kv">
            <dt>SMS (Twilio)</dt>
            <dd>
              {summary ? (
                <Badge variant={summary.smsConfigured ? "success" : "warning"}>
                  {summary.smsConfigured ? "Configuré" : "Simulation"}
                </Badge>
              ) : (
                "—"
              )}
            </dd>
            <dt>Courriel (Mailjet)</dt>
            <dd>
              {summary ? (
                <Badge variant={summary.emailConfigured ? "success" : "warning"}>
                  {summary.emailConfigured ? "Configuré" : "Simulation"}
                </Badge>
              ) : (
                "—"
              )}
            </dd>
            <dt>Expéditeur des courriels</dt>
            <dd>{summary?.emailSender ?? "— (MAILJET_FROM_EMAIL absent)"}</dd>
          </dl>
          <p className="reglages__note">
            En simulation, chaque message est stocké dans la boîte d'envoi avec le statut « Simulé »
            ; rien n'est transmis au client.
          </p>
          <div className="form-grid">
            <div className="form-grid__full">
              <Field
                label="Envoyer un courriel de test"
                hint="Vérifie l'expéditeur, le gabarit HTML et les clés. Le message apparaît dans la boîte d'envoi."
              >
                <Input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="vous@exemple.com"
                />
              </Field>
            </div>
            <div className="form-grid__full">
              <Button
                variant="ghost"
                onClick={() => void test()}
                disabled={testing || !testTo.trim()}
              >
                {testing ? "Envoi…" : "Envoyer le test"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
